/**
 * Baseline stage (§5f + §5e) — what "current" means: the working timetable the
 * design is measured against, plus the line geography that drives per-mile costing.
 *
 * When thebigplan is reachable (§5e) this shows the REAL planned working timetable
 * pulled cross-service from thebigplan — the actual operating plan. When it is
 * not, it falls back to a sales-derived reconstruction and says so on screen
 * (MASTER_PLAN §6.6). Line geography is read from network.ts, never re-keyed.
 */
import type { ReactNode } from 'react'
import type { Departure, Network } from '../types'
import type { BaselineWtt } from '../baseline'
import { fmtMin } from '../demand'
import { routeLabel } from '../network'
import { WttGrid } from '../WttGrid'

export interface BaselineStageProps {
  net: Network
  /** The sales-derived baseline departures (fallback / demand baseline). */
  deps: Departure[]
  /** The real thebigplan WTT, when available. */
  wtt?: BaselineWtt | null
  onPickPattern?: (code: string) => void
  /** Re-homed WTT XLSX export (TTBuilder format) for the baseline (MASTER_PLAN §4). */
  xlsxExport?: ReactNode
  /** Tune a working's origin departure — writes to the DESIGN copy only. */
  onTuneDep?: (id: string, hhmm: string) => void
}

function Geography({ net }: { net: Network }) {
  const nymrMiles = net.segments.filter((s) => s.owner === 'nymr').reduce((t, s) => t + s.miles, 0)
  const nrMiles = net.segments.filter((s) => s.owner === 'networkrail').reduce((t, s) => t + s.miles, 0)
  return (
    <section className="sh-card sh-card--pad sh-mt">
      <div className="sh-section-label">Line geography — costed per mile between each station</div>
      <div className="sh-geo">
        {net.segments.map((sg, i) => (
          <div className="sh-geo-seg" key={i} style={{ flex: sg.miles }}>
            <div className="sh-geo-mi">{sg.miles} mi</div>
            <div className={`sh-geo-bar ${sg.owner === 'nymr' ? 'sh-geo-bar--nymr' : 'sh-geo-bar--nr'}`} />
            <div className="sh-geo-owner">{sg.owner === 'nymr' ? 'NYMR' : 'Network Rail'}</div>
          </div>
        ))}
      </div>
      <div className="sh-geo-scale">
        {net.stations.map((s, i) => <span key={s.code}>{s.tt} {net.cumMiles[i].toFixed(1)}</span>)}
      </div>
      <p className="stu-note sh-mt">
        {nymrMiles.toFixed(1)} mi on NYMR metals + {nrMiles.toFixed(1)} mi over Network Rail. Fuel, running
        maintenance, overhaul provision and P-way/S&amp;T all accrue per mile run over each segment.
      </p>
    </section>
  )
}

export function BaselineStage({ net, deps, wtt, onPickPattern, xlsxExport, onTuneDep }: BaselineStageProps) {
  // ---- REAL thebigplan WTT ----
  if (wtt?.available && wtt.workings) {
    const t = wtt.timetable
    // The payload carries EVERY pattern's workings — the grid shows the selected one.
    const mine = wtt.workings.filter((w) => !wtt.selectedPattern || w.pattern_code === wtt.selectedPattern)
    const patternMeta = wtt.patterns?.find((p) => p.code === wtt.selectedPattern) ?? null
    return (
      <>
        <section className="sh-banner sh-banner--info">
          <span className="sh-banner-icon">🔗</span>
          <div>
            <div className="sh-banner-title">Synced from thebigplan — the actual planned working timetable</div>
            <div className="sh-banner-body">
              {t ? <>{t.name} · <strong>{t.status}</strong> · effective {t.effective_from} → {t.effective_to} · read-only.</> : 'read-only.'}{' '}
              Contribution figures in Compare and Results use the calibrated demand baseline (reconstructed from observed sales); this is the operating plan the design is measured against.
            </div>
          </div>
        </section>

        {wtt.patterns && wtt.patterns.length > 0 && (
          <div className="sh-pats">
            <span className="stu-note">Pattern:</span>
            {wtt.patterns.map((p) => (
              <button
                key={p.code} type="button"
                className={`sh-pat${p.code === wtt.selectedPattern ? ' sh-pat--active' : ''}`}
                title={`${p.name}${p.season ? ` · ${p.season}` : ''} · ${p.workings} workings`}
                onClick={() => onPickPattern?.(p.code)}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        <section className="sh-card sh-card--pad">
          <div className="sh-card-head" style={{ padding: 0, marginBottom: 6 }}>
            <span className="sh-card-title">Working timetable — TTBuilder view</span>
            {xlsxExport && <span className="sh-card-note">{xlsxExport}</span>}
          </div>
          {/* thebigplan's TTBuilder grid, uplifted — the layout the railway
              builds and tunes timetables in. */}
          <WttGrid workings={mine} pattern={patternMeta} season={patternMeta?.season} onTuneDep={onTuneDep} />
          {mine.length === 0 && <p className="stu-note">No workings in this pattern.</p>}
        </section>

        <Geography net={net} />
      </>
    )
  }

  // ---- sales-derived fallback (flagged) ----
  const rows = [...deps].sort((a, b) => a.minutes - b.minutes)
  const timeAt = (d: Departure, i: number): string => {
    const lo = Math.min(d.o, d.d), hi = Math.max(d.o, d.d)
    if (i < lo || i > hi) return '—'
    return fmtMin(d.minutes + Math.abs(net.cumMin[i] - net.cumMin[d.o]))
  }
  return (
    <>
      <section className="sh-banner sh-banner--warn">
        <span className="sh-banner-icon">🔗</span>
        <div>
          <div className="sh-banner-title">Baseline reconstructed from observed sales</div>
          <div className="sh-banner-body">
            The real thebigplan working timetable is not available{wtt?.reason ? ` (${wtt.reason})` : ''}, so “current” is
            reconstructed from observed ticket sales and flagged here per the honesty rules.
          </div>
        </div>
      </section>

      <section className="sh-card">
        <div className="sh-card-head">
          <span className="sh-card-title">Working timetable — as drawn (sales-derived)</span>
          {xlsxExport && <span className="sh-card-note">{xlsxExport}</span>}
        </div>
        <div className="stu-scroll">
          <table className="stu-table">
            <thead>
              <tr>
                <th>Dir</th><th>Route</th>
                {net.stations.map((s) => <th key={s.code} className="num">{s.tt}</th>)}
                <th className="num">Coaches</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>{d.d > d.o ? 'N' : 'S'}</td>
                  <td>{routeLabel(net, d.o, d.d)}</td>
                  {net.stations.map((s, i) => <td key={s.code} className="num sh-mono">{timeAt(d, i)}</td>)}
                  <td className="num sh-mono">{d.coaches === 0 ? 'RC' : d.coaches}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={net.stations.length + 3} className="stu-note">No baseline departures for this slice.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Geography net={net} />
    </>
  )
}
